"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PackForSaleRow } from "@/actions/packs";
import type { FeedResponse } from "@/actions/post";
import type { ShopItemForBuyer } from "@/actions/shop";
import type { EntityStoryStatus } from "@/actions/story";
import type { HiiveBookableNow } from "@/actions/venue-discovery";
import type { PublicVenueSummary } from "@/actions/venue-public";
import type { CommentsSheetViewer } from "@/components/posts/comments-sheet";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import { DETAIL_DESKTOP } from "@/components/shell/detail-page-desktop";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import type { MediaData } from "@/db/schema/hiive";
import { useBackNavigation } from "@/hooks/use-back-navigation";
import {
  DISABLE_DRAWER_ANIMATION_CLASS,
  useDisableInitialDrawerAnimation,
} from "@/hooks/use-disable-initial-drawer-animation";
import type { HiiveEventsData } from "@/hooks/use-hiive-data";
import { useIsDesktop, useWindowScrolledPast } from "@/hooks/use-is-desktop";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { ASPECT_RATIO_VALUE } from "@/lib/aspect-ratios";
import { resolveBackHref } from "@/lib/back-nav";
import { goBackTo } from "@/lib/go-back";
import type { Coordinates } from "@/lib/location-ranking";
import { detailSheetSnapPoints } from "@/lib/ui/detail-sheet-snap-points";
import { attachSheetPullHandoff } from "@/lib/ui/sheet-pull-handoff";
import { cn } from "@/lib/utils";
import { HiiveDrawerBody } from "./hiive-drawer-body";
import { HiiveFooterCta } from "./hiive-footer-cta";
import { HiiveHeroSection } from "./hiive-hero-section";
import type { HiiveMemberListItem } from "./hiive-members.shared";
import type { Hiive, MembershipState } from "./hiive-page.types";
import { HiiveStickyHeader } from "./hiive-sticky-header";

interface Props {
  hiive: Hiive;
  initialViewerLocation: Coordinates | null;
  bannerMedia: MediaData[];
  hiiveSlug: string;
  membershipState: MembershipState;
  viewer: CommentsSheetViewer;
  isAuthenticated: boolean;
  initialFeed: FeedResponse;
  initialEvents: HiiveEventsData;
  initialMembers: HiiveMemberListItem[];
  initialVenues: PublicVenueSummary[];
  initialBookableNow: HiiveBookableNow;
  initialShopItems: ShopItemForBuyer[];
  packs: PackForSaleRow[];
  storyRingStatus: EntityStoryStatus | null;
  reviewSummary: { rating: number; count: number } | null;
}

const HERO_DRAWER_DIRECTION_LOCK_PX = 8;
const HERO_DRAWER_EXPAND_THRESHOLD_PX = 24;

export function HiivePageClient({
  hiive,
  initialViewerLocation,
  bannerMedia,
  hiiveSlug,
  membershipState,
  viewer,
  isAuthenticated,
  initialFeed,
  initialEvents,
  initialMembers,
  initialVenues,
  initialBookableNow,
  initialShopItems,
  packs,
  storyRingStatus,
  reviewSummary,
}: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const fromParam = searchParams.get("from");
  const backHref = useMemo(
    () => resolveBackHref(fromParam, "/hiives"),
    [fromParam],
  );
  // Not a bare `router.push`: that never staged a scroll restore, so every
  // back out of a hiive dropped the reader at the top of a rebuilt /hiives
  // with its carousels rewound to the first card.
  const handleBrowserBack = useCallback(() => {
    goBackTo(router, backHref);
  }, [backHref, router]);

  useBackNavigation({
    behavior: "intercept",
    onBack: handleBrowserBack,
    cleanupOnUnmount: false,
  });
  // Desktop drops the snap-point drawer entirely: the hero sits in a sticky
  // left column and the body renders inline beside it (see DETAIL_DESKTOP).
  // Everything drawer-shaped below still runs, but only the phone reads it.
  const isDesktop = useIsDesktop();
  // Default snap points for server rendering and initial client render
  const [snapPoints, setSnapPoints] = useState<string[]>(["500px", "700px"]);
  const [snap, setSnap] = useState<number | string | null>("500px");
  const snapPointsRef = useRef(snapPoints);
  snapPointsRef.current = snapPoints;
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const expandDrawer = useCallback(() => {
    const expanded = snapPointsRef.current[1];
    if (expanded != null) setSnap(expanded);
  }, []);
  const { setManualPause } = useVideoAutoplay();
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const heroTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const heroTouchAxisRef = useRef<"x" | "y" | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const sheetPullHandoffCleanupRef = useRef<(() => void) | null>(null);
  const hasEvents =
    initialEvents.upcoming.length > 0 || initialEvents.past.length > 0;
  const hasVenues = initialVenues.length > 0;
  const hasShop = initialShopItems.length > 0;
  /**
   * Tab order follows what a member can ACT on, not a fixed list.
   *
   * Venues with bookable services and events that have not happened yet lead;
   * Members drops behind them. Landing on "Members" while the venue has open
   * slots buries the one thing on the page you can actually do — and a hiive
   * whose events are all in the past should not lead with Events either.
   *
   * `experienceCount` counts published bookable services, which is the cheapest
   * honest proxy for "something here is bookable" — the /hiives "Open to book"
   * rail is what resolves real slot availability.
   */
  const hasBookableVenues = initialVenues.some(
    (venueSummary) => venueSummary.experienceCount > 0,
  );
  const hasUpcomingEvents = initialEvents.upcoming.length > 0;
  const visibleTabValues = useMemo(() => {
    const leading: string[] = [];
    if (hasBookableVenues) leading.push("venues");
    if (hasUpcomingEvents) leading.push("events");

    const trailing: string[] = [];
    if (hasVenues && !leading.includes("venues")) trailing.push("venues");
    if (hasEvents && !leading.includes("events")) trailing.push("events");
    if (hasShop) trailing.push("shop");

    return [...leading, "members", "reviews", ...trailing, "posts"];
  }, [hasBookableVenues, hasUpcomingEvents, hasEvents, hasVenues, hasShop]);
  /** First tab in the resolved order — no longer always "members". */
  const defaultTab = visibleTabValues[0] ?? "members";
  const normalizeTab = useCallback(
    (tab: string | null) =>
      tab && visibleTabValues.includes(tab) ? tab : defaultTab,
    [visibleTabValues, defaultTab],
  );

  // Initialize state from URL, default to the first tab in the resolved order.
  const [currentTab, setCurrentTab] = useState(() =>
    normalizeTab(searchParams.get("tab")),
  );
  // Direct links to a non-default tab should expose that tab's content rather
  // than leave it behind the collapsed hero drawer.
  const initialTabNeedsExpandedDrawerRef = useRef(
    normalizeTab(searchParams.get("tab")) !== defaultTab,
  );

  const updateUrl = useCallback(
    (tab: string) => {
      const params = new URLSearchParams(searchParams.toString());
      // Omit the param for whichever tab is the default here, not for
      // "members" specifically — that is no longer always the landing tab.
      if (tab === defaultTab) {
        params.delete("tab");
      } else {
        params.set("tab", tab);
      }
      const query = params.toString();
      // Shallow query update on the same pathname – relative `?query`
      // keeps it a search-param change, not a route change.
      const newUrl = query ? `?${query}` : pathname;
      window.history.pushState(null, "", newUrl);
    },
    [pathname, searchParams, defaultTab],
  );

  const tabsRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (value: string) => {
    if (!visibleTabValues.includes(value)) return;
    captureClientEvent(ANALYTICS_EVENTS.HIIVE_TAB_CHANGED, {
      hiive_id: hiive.id,
      hiive_name: hiive.name,
      hiive_slug: hiiveSlug,
      tab: value,
    });
    setCurrentTab(value);
    updateUrl(value);
    expandDrawer();
  };

  // Sync state with URL changes (e.g. back button)
  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    const tab = normalizeTab(requestedTab);
    setCurrentTab(tab);
    if (requestedTab && requestedTab !== tab) {
      updateUrl(tab);
    }
  }, [normalizeTab, searchParams, updateUrl]);

  // One view per visit, not one per Activity re-show. Next 16 hides the route
  // you navigate away from instead of unmounting it, and React destroys and
  // re-creates effects across that hide/show — so an unguarded capture here
  // counts a fresh view every time the user comes back. The ref is the right
  // guard because Activity preserves state and refs; only effects are torn
  // down. Same pattern as EVENT_VIEWED on the event page.
  const trackedViewRef = useRef(false);
  useEffect(() => {
    if (trackedViewRef.current) return;
    trackedViewRef.current = true;
    captureClientEvent(ANALYTICS_EVENTS.HIIVE_VIEWED, {
      hiive_id: hiive.id,
      hiive_name: hiive.name,
      hiive_slug: hiiveSlug,
    });
  }, [hiive.id, hiive.name, hiiveSlug]);

  const [showStickyTitle, setShowStickyTitle] = useState(false);
  // The drawer reports its own scroll through `onUpdate`; the desktop page
  // scrolls the window, past the title at the top of the right column.
  const windowScrolledPastTitle = useWindowScrolledPast(120, isDesktop);

  // Stable on purpose: it only reads a ref and calls a state setter. It used to
  // be re-created every render, which made `attachRef` a new callback ref each
  // render too — so React detached and re-attached every listener (and a new
  // ResizeObserver) on each render, mid-gesture included.
  const onUpdate = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    // Show the sticky title once the sheet has scrolled past the header block.
    setShowStickyTitle((el.scrollTop ?? 0) > 200);
  }, []);

  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  const [tabsHeight, setTabsHeight] = useState(64); // Default to approx height to avoid 0

  // Measure tabs height
  useLayoutEffect(() => {
    if (!tabsRef.current) return;

    // Immediate measurement
    setTabsHeight(tabsRef.current.offsetHeight);

    const observer = new ResizeObserver(() => {
      if (tabsRef.current) {
        setTabsHeight(tabsRef.current.offsetHeight);
      }
    });
    observer.observe(tabsRef.current);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!headerRef.current) return;

    const observer = new ResizeObserver(() => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    });

    observer.observe(headerRef.current);

    // Initial measurement
    setHeaderHeight(headerRef.current.offsetHeight);

    return () => observer.disconnect();
  }, []);

  const [containerHeight, setContainerHeight] = useState(0);

  const attachRef = useCallback(
    (el: HTMLDivElement | null) => {
      // detach from previous node
      nodeRef.current?.removeEventListener("scroll", onUpdate);
      sheetPullHandoffCleanupRef.current?.();
      sheetPullHandoffCleanupRef.current = null;

      nodeRef.current = el;
      resizeObserverRef.current?.disconnect();

      // attach to new node
      if (!el) {
        return;
      }

      // Create observer for container height
      const observer = new ResizeObserver(() => {
        setContainerHeight(el.clientHeight);
      });
      observer.observe(el);
      resizeObserverRef.current = observer;
      setContainerHeight(el.clientHeight);

      onUpdate();
      el.addEventListener("scroll", onUpdate, { passive: true });
      // No `data-vaul-no-drag` toggling here any more: vaul already refuses to
      // drag while this scroller is off its top, and checks at move time
      // rather than at pointerdown. What vaul cannot do on its own is keep the
      // browser from claiming a pull-down at the top — see the helper.
      sheetPullHandoffCleanupRef.current = attachSheetPullHandoff(el);
    },
    [onUpdate],
  );

  // Ensure tab content can fill the drawer when the snap point is expanded
  const minContentHeight =
    containerHeight > 0
      ? containerHeight - headerHeight - tabsHeight - 80 - 20
      : 0;

  // Capture initial open/focus intent once to avoid collapsing on tab changes
  const initialShouldOpenRef = useRef(
    searchParams.get("open_drawer") === "true",
  );
  const initialShouldFocusTabsRef = useRef(
    searchParams.get("focus_tabs") === "true",
  );

  useLayoutEffect(() => {
    // Calculate snap points from current viewport and hero height
    const topHeight =
      heroRef.current?.offsetHeight ??
      Math.floor(
        window.innerWidth * ASPECT_RATIO_VALUE.portrait5By4HeightMultiplier,
      );

    const points = detailSheetSnapPoints(topHeight, window.innerHeight);
    setSnapPoints(points);

    const shouldOpen = initialShouldOpenRef.current;
    const shouldFocusTabs = initialShouldFocusTabsRef.current;

    if (
      shouldOpen ||
      shouldFocusTabs ||
      initialTabNeedsExpandedDrawerRef.current
    ) {
      setSnap(points[1]);
    } else {
      setSnap(points[0]);
    }
  }, []);

  // Rotating or unfolding (iPhone Duo) changes the hero and the screen, so the
  // mount-time points would park the sheet off-screen. Same as the event page.
  useEffect(() => {
    const onResize = () => {
      const topHeight =
        heroRef.current?.offsetHeight ??
        Math.floor(
          window.innerWidth * ASPECT_RATIO_VALUE.portrait5By4HeightMultiplier,
        );
      const points = detailSheetSnapPoints(topHeight, window.innerHeight);
      const index = snapPointsRef.current.indexOf(snapRef.current as string);
      setSnapPoints(points);
      if (index >= 0) setSnap(points[index]);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const heroElement = heroRef.current;
    const expandedSnapPoint = snapPoints[1];
    if (!heroElement || expandedSnapPoint == null) return;

    const resetHeroGesture = () => {
      heroTouchStartRef.current = null;
      heroTouchAxisRef.current = null;
    };

    const expandDrawer = () => {
      if (snap === expandedSnapPoint) return;
      setSnap(expandedSnapPoint);
    };

    const handleWheel = (event: WheelEvent) => {
      if (
        Math.abs(event.deltaY) <= Math.abs(event.deltaX) ||
        event.deltaY <= 0
      ) {
        return;
      }

      expandDrawer();
    };

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;

      heroTouchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      heroTouchAxisRef.current = null;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      const start = heroTouchStartRef.current;
      if (!touch || !start) return;

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;

      if (
        heroTouchAxisRef.current == null &&
        Math.max(Math.abs(deltaX), Math.abs(deltaY)) >=
          HERO_DRAWER_DIRECTION_LOCK_PX
      ) {
        heroTouchAxisRef.current =
          Math.abs(deltaY) > Math.abs(deltaX) ? "y" : "x";
      }

      if (
        heroTouchAxisRef.current === "y" &&
        deltaY <= -HERO_DRAWER_EXPAND_THRESHOLD_PX
      ) {
        expandDrawer();
        resetHeroGesture();
      }
    };

    heroElement.addEventListener("wheel", handleWheel, { passive: true });
    heroElement.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    heroElement.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    });
    heroElement.addEventListener("touchend", resetHeroGesture, {
      passive: true,
    });
    heroElement.addEventListener("touchcancel", resetHeroGesture, {
      passive: true,
    });

    return () => {
      heroElement.removeEventListener("wheel", handleWheel);
      heroElement.removeEventListener("touchstart", handleTouchStart);
      heroElement.removeEventListener("touchmove", handleTouchMove);
      heroElement.removeEventListener("touchend", resetHeroGesture);
      heroElement.removeEventListener("touchcancel", resetHeroGesture);
    };
  }, [snap, snapPoints]);

  // Only allow hero video while the drawer is on the collapsed snap. Any other
  // snap (fully expanded, intermediate, or null during remount) must pause —
  // IntersectionObserver still sees the hero as "in viewport" under the drawer
  // because the drawer is portaled and does not occlude for intersection.
  useEffect(() => {
    // Nothing covers the hero on desktop, so nothing should pause it.
    if (isDesktop) {
      setManualPause(false);
      return;
    }
    if (snapPoints.length < 1) return;
    const isCollapsed = snap === snapPoints[0];
    setManualPause(!isCollapsed);
  }, [isDesktop, snap, snapPoints, setManualPause]);

  useEffect(() => {
    return () => {
      setManualPause(false);
    };
  }, [setManualPause]);

  // Initial state logic
  const shouldFocusTabs = searchParams.get("focus_tabs") === "true";
  const [isReady, setIsReady] = useState(!shouldFocusTabs);
  const isInitialDrawerAnimationDisabled = useDisableInitialDrawerAnimation();

  // Handle focus_tabs action
  useLayoutEffect(() => {
    // If not focusing tabs, we are ready immediately
    if (!shouldFocusTabs) {
      setIsReady(true);
      return;
    }

    // Desktop has no drawer to expand — the tabs are already on the page, so
    // scroll the window to them and reveal.
    if (isDesktop) {
      const tabs = tabsRef.current;
      if (tabs) {
        window.scrollTo({
          top: Math.max(
            0,
            tabs.getBoundingClientRect().top + window.scrollY - headerHeight,
          ),
          behavior: "instant",
        });
      }
      setIsReady(true);
      return;
    }

    // Ensure we only scroll when drawer is fully open (snap is at max)
    if (
      headerHeight > 0 &&
      tabsRef.current &&
      nodeRef.current &&
      snap === snapPoints[1]
    ) {
      // Use double RAF to ensure layout/paint has settled after snap change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (nodeRef.current && tabsRef.current) {
            const stickyTop =
              parseFloat(
                getComputedStyle(tabsRef.current).top?.replace("px", "") ?? "0",
              ) || Math.max(0, headerHeight - 57);
            const elementOffset = tabsRef.current.offsetTop;
            const targetScroll = elementOffset - stickyTop - 45;

            nodeRef.current.scrollTo({
              top: targetScroll,
              behavior: "instant",
            });

            // Reveal content after scroll
            requestAnimationFrame(() => setIsReady(true));
          }
        });
      });
    }
  }, [isDesktop, shouldFocusTabs, headerHeight, snap, snapPoints]);

  const drawerBodyProps = {
    visibleTabValues,
    hiive,
    initialViewerLocation,
    hiiveSlug,
    viewer,
    currentTab,
    onTabChange: handleTabChange,
    onExpandDrawer: expandDrawer,
    headerHeight,
    tabsRef,
    minContentHeight,
    isReady,
    snap,
    snapPoints,
    attachRef,
    isAuthenticated,
    initialFeed,
    initialEvents,
    initialMembers,
    initialVenues,
    initialBookableNow,
    initialShopItems,
    packs,
    storyRingStatus,
    reviewSummary,
  };

  return (
    <div
      className={cn(
        "mx-auto flex h-screen max-w-2xl flex-col overflow-x-clip overscroll-x-none",
        DETAIL_DESKTOP.grid,
      )}
    >
      <HiiveStickyHeader
        ref={headerRef}
        hiive={hiive}
        showStickyTitle={isDesktop ? windowScrolledPastTitle : showStickyTitle}
      />

      <div className={DETAIL_DESKTOP.mediaColumn}>
        <HiiveHeroSection
          hiiveName={hiive.name}
          bannerMedia={bannerMedia}
          isReady={isReady}
          heroRef={heroRef}
          className={DETAIL_DESKTOP.media}
        />
        {isDesktop ? (
          <HiiveFooterCta
            hiive={hiive}
            hiiveSlug={hiiveSlug}
            membershipState={membershipState}
            variant="inline"
          />
        ) : null}
      </div>

      {isDesktop ? (
        <div className={DETAIL_DESKTOP.content}>
          <HiiveDrawerBody layout="page" {...drawerBodyProps} />
        </div>
      ) : (
        /* Drawer with snap points */
        <Drawer
          dismissible={false}
          modal={false}
          snapPoints={snapPoints}
          activeSnapPoint={snap}
          setActiveSnapPoint={setSnap}
          defaultOpen
        >
          <DrawerContent
            showHandle={false}
            className={cn(
              isInitialDrawerAnimationDisabled &&
                DISABLE_DRAWER_ANIMATION_CLASS,
              // `page-sheet`: the page content, not a modal (globals.css).
              // No focus ring: it takes focus on open, and WebKit outlined
              // the whole page in blue.
              "page-sheet mt-0 h-full overflow-hidden border-none bg-transparent text-text-primary outline-none",
            )}
          >
            <HiiveDrawerBody {...drawerBodyProps} />
          </DrawerContent>
        </Drawer>
      )}

      {isDesktop ? null : (
        <HiiveFooterCta
          hiive={hiive}
          hiiveSlug={hiiveSlug}
          membershipState={membershipState}
        />
      )}
    </div>
  );
}
